import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Gauge, Trophy, Check, ArrowRight, Timer, Target, Sparkles,
  Play, Phone, MessageCircle, Mail, Instagram, Facebook, Youtube,
  Users, Star, Clock, Volume2, VolumeX,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import VideoModal from "@/components/VideoModal";
import { toStreamableVideoUrl } from "@/lib/videoUrl";

const RIG_IMG = "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1600&q=80";

// TikTok icon (lucide doesn't ship one)
function TikTokIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.16 8.16 0 0 0 4.77 1.52V6.31a4.85 4.85 0 0 1-1.84-.36 5 5 0 0 1 0 .74Z"/>
    </svg>
  );
}

export default function Simulator() {
  const { t } = useLang();
  const [site, setSite] = useState({});
  const [videoOpen, setVideoOpen] = useState(false);
  const [heroMuted, setHeroMuted] = useState(true);
  const heroVideoRef = useRef(null);
  const [sessions, setSessions] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [bookingForm, setBookingForm] = useState({ name: "", email: "", phone: "", level: "amateur", circuit_pref: "", message: "" });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [qualifyStep, setQualifyStep] = useState(false);
  const [qualifyChoice, setQualifyChoice] = useState("");
  const [qualifySending, setQualifySending] = useState(false);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/sim-sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, []);

  // Deep-link : /simulator?s=<session_id> ouvre automatiquement le dialog de réservation
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("s");
    if (!sid || sessions.length === 0) return;
    const s = sessions.find((x) => x.id === sid);
    if (s) {
      openBooking(s);
      // clean URL after opening (garde l'ancre reserver)
      navigate("/simulator#reserver", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, location.search]);

  const openBooking = (session) => {
    setSelectedSession(session || null);
    setBookingForm({ name: "", email: "", phone: "", level: "amateur", circuit_pref: session?.circuit || "", message: "" });
    setQualifyStep(false);
    setQualifyChoice("");
    setBookingOpen(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setBookingSubmitting(true);
    try {
      await api.post("/coaching-bookings", {
        session_id: selectedSession?.id || null,
        ...bookingForm,
      });
      const { toast } = await import("sonner");
      toast.success("Demande envoyée — nous vous recontactons rapidement.");
      // Passer à l'étape de qualification (au lieu de fermer le dialog)
      setQualifyStep(true);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error("Erreur — réessayez ou contactez-nous directement.");
    } finally {
      setBookingSubmitting(false);
    }
  };

  const submitQualify = async (choice) => {
    setQualifyChoice(choice);
    setQualifySending(true);
    try {
      await api.post("/leads", {
        email: bookingForm.email,
        vehicle_recherche: choice,
        source: "sim_qualification",
        name: bookingForm.name,
        phone: bookingForm.phone,
      });
      const { toast } = await import("sonner");
      toast.success("Merci ! On vous tiendra informé.");
      setTimeout(() => setBookingOpen(false), 1200);
    } catch {
      // best-effort — on ferme quand même
      setTimeout(() => setBookingOpen(false), 800);
    } finally {
      setQualifySending(false);
    }
  };

  const blocks = [
    { icon: Target, title: t.simulator.block1_title, text: t.simulator.block1_text },
    { icon: Timer, title: t.simulator.block2_title, text: t.simulator.block2_text },
    { icon: Sparkles, title: t.simulator.block3_title, text: t.simulator.block3_text },
  ];

  const reels = [site.sim_reel_1, site.sim_reel_2, site.sim_reel_3].filter(Boolean);
  const extractYtId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : null;
  };
  const youtubeIds = [site.sim_youtube_1, site.sim_youtube_2, site.sim_youtube_3].map(extractYtId).filter(Boolean);
  const reviews = (site.sim_reviews && site.sim_reviews.length)
    ? site.sim_reviews.filter((r) => r && (r.quote || "").trim() && (r.name || "").trim())
    : [];
  const prices = (site.sim_prices && site.sim_prices.length) ? site.sim_prices : [];
  const waHref = site.whatsapp
    ? `https://wa.me/${site.whatsapp.replace(/[^\d]/g, "")}?text=${encodeURIComponent("Bonjour KV, je souhaite réserver une session simulateur.")}`
    : "#";
  const telHref = site.phone ? `tel:${site.phone.replace(/\s+/g, "")}` : "#";
  const mailHref = site.email ? `mailto:${site.email}?subject=${encodeURIComponent("Réservation session simulateur")}` : "#";

  return (
    <div data-testid="simulator-page" className="bg-[#0D0D0D] min-h-screen">
      <SEO title="Coaching pilotage simulateur pro · Le Plus KV International" description="Coaching pilotage sur simulateur professionnel — le service Plus de KV International, spécialiste de l'achat et vente de Porsche de prestige en Belgique. Circuits mondiaux, télémétrie temps réel, Rhode-Saint-Genèse." />
      {/* HERO with looping background video */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {site.sim_video_url ? (
            <video
              key={site.sim_video_url}
              ref={heroVideoRef}
              src={toStreamableVideoUrl(site.sim_video_url)}
              autoPlay muted loop playsInline
              preload="metadata"
              className="w-full h-full object-cover img-fade bg-[#0D0D0D]"
              data-testid="sim-hero-video"
              aria-hidden="true"
            />
          ) : (
            <div className="w-full h-full bg-[#0D0D0D]" aria-hidden="true" />
          )}
          {/* Overlays multi-couches — voile noir + gradient bas + gradient latéral pour préserver le centre de la vidéo */}
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-transparent" />
        </div>

        {/* Bouton mute/unmute discret en coin */}
        {site.sim_video_url && (
          <button
            type="button"
            data-testid="sim-hero-mute-toggle"
            aria-label={heroMuted ? "Activer le son de la vidéo" : "Couper le son de la vidéo"}
            onClick={() => {
              const el = heroVideoRef.current;
              if (!el) return;
              const next = !heroMuted;
              el.muted = next;
              if (!next) {
                try { el.play(); } catch {}
              }
              setHeroMuted(next);
            }}
            className="absolute bottom-6 right-6 z-20 w-11 h-11 rounded-full bg-black/60 backdrop-blur-md border border-white/20 hover:border-[#C8A75A] hover:bg-black/80 text-white/80 hover:text-[#C8A75A] flex items-center justify-center transition-colors shadow-lg"
            title={heroMuted ? "Activer le son" : "Couper le son"}
          >
            {heroMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 pt-32 w-full">
          <div className="max-w-xl lg:max-w-2xl">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C8A75A] mb-4 fade-up flex items-center gap-3">
              <Gauge className="w-4 h-4" /> Coaching Sim Pro · Perfectionnement sur circuit
            </p>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] font-light text-white fade-up fade-up-delay-1 drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]">
              {(site.sim_hero_title && (site.sim_hero_title.fr || site.sim_hero_title.en)) ? (site.sim_hero_title.fr || site.sim_hero_title.en) : t.simulator.hero_title}
            </h1>
            <p className="text-white/80 text-base md:text-lg mt-6 leading-relaxed fade-up fade-up-delay-2 font-light">
              Simulateur dynamique de niveau professionnel · Coaching individuel par pilotes experts · Circuits mondiaux à la carte · Télémétrie en temps réel.
            </p>
            <div className="flex flex-wrap gap-4 mt-8 fade-up fade-up-delay-3">
              <button type="button" onClick={() => openBooking(null)} data-testid="sim-hero-book" className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-semibold px-8 py-5 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2 shadow-[0_8px_32px_-8px_rgba(200,167,90,0.6)]">
                Réserver ma session <ArrowRight className="w-4 h-4" />
              </button>
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="sim-hero-wa" className="border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-black transition-colors duration-300 px-8 py-5 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING WIDGET — 3 quick channels */}
      <section data-testid="sim-booking-widget" className="relative -mt-16 z-20 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="bg-[#1E1E1E] border border-[#C8A75A]/30 rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-6 md:p-8 grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-4 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-2">{t.simulator.book_now}</p>
            <p className="font-display text-xl md:text-2xl text-white leading-tight">Réservez votre session dès maintenant</p>
            <p className="text-white/50 text-sm mt-1">{site.hours?.fr || "Uniquement sur rendez-vous · 7j/7"}</p>
          </div>
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="book-wa" className="flex items-center justify-center gap-3 bg-[#25D366] text-black hover:bg-white transition-colors px-6 py-5 rounded-md font-medium">
            <MessageCircle className="w-5 h-5" />
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest opacity-70">{t.simulator.book_wa}</p>
              <p className="text-sm font-bold">{site.phone || "Chat"}</p>
            </div>
          </a>
          <a href={telHref} data-testid="book-call" className="flex items-center justify-center gap-3 bg-[#C8A75A] text-black hover:bg-white transition-colors px-6 py-5 rounded-md font-medium">
            <Phone className="w-5 h-5" />
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest opacity-70">{t.simulator.book_call}</p>
              <p className="text-sm font-bold">{site.phone || "-"}</p>
            </div>
          </a>
          <Link to="/contact?type=simulator" data-testid="book-form" className="flex items-center justify-center gap-3 border border-white/20 text-white hover:bg-white/5 transition-colors px-6 py-5 rounded-md">
            <Mail className="w-5 h-5 text-[#C8A75A]" />
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest text-white/50">{t.simulator.book_form}</p>
              <p className="text-sm font-medium">Programme sur mesure</p>
            </div>
          </Link>
        </div>
      </section>

      {/* PROCHAINES SESSIONS — calendrier public */}
      <section id="reserver" data-testid="sim-sessions-public" className="py-32 bg-[#0D0D0D] scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Calendrier · Coaching sur circuit</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white leading-[1.05]">Prochaines sessions</h2>
              <p className="text-white/60 mt-4 max-w-2xl">Perfectionnez-vous aux côtés de pilotes experts sur les circuits mondiaux. Chaque session inclut briefing personnalisé, coaching en direct et débriefing télémétrique.</p>
            </div>
            <button type="button" onClick={() => openBooking(null)} data-testid="sim-book-custom" className="bg-transparent border border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black uppercase tracking-widest text-xs px-6 py-4 rounded-md">
              Demander une session sur mesure
            </button>
          </div>

          {sessions.length === 0 ? (
            <div className="border border-white/10 rounded-md p-10 text-center text-white/50">
              <p className="mb-4">Aucune session au calendrier pour l'instant.</p>
              <button type="button" onClick={() => openBooking(null)} className="bg-[#C8A75A] text-black hover:bg-white px-6 py-3 rounded-md uppercase tracking-widest text-xs">Demander un créneau</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sessions.map((s) => {
                const full = s.booked >= s.capacity || s.status !== "open";
                const dateObj = new Date(s.date + "T" + (s.time || "12:00"));
                const dayLabel = dateObj.toLocaleDateString("fr-BE", { weekday: "long", day: "numeric", month: "long" });
                return (
                  <div key={s.id} data-testid={`sim-session-${s.id}`} className={`bg-[#1E1E1E] border rounded-md p-6 flex flex-col ${full ? "border-white/5 opacity-70" : "border-white/10 hover:border-[#C8A75A]/50"} transition-colors`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{s.module}</p>
                        <p className="font-display text-xl text-white mt-1 capitalize">{dayLabel}</p>
                        <p className="text-white/60 text-sm mt-1">{s.time} · {s.duration_min} min</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${full ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}>{full ? "Complet" : "Ouvert"}</span>
                    </div>
                    <div className="space-y-2 text-sm text-white/70 mb-6">
                      {s.circuit && <p><span className="text-white/40 text-xs uppercase tracking-widest mr-2">Circuit</span>{s.circuit}</p>}
                      {s.coach && <p><span className="text-white/40 text-xs uppercase tracking-widest mr-2">Coach</span>{s.coach}</p>}
                      <p><span className="text-white/40 text-xs uppercase tracking-widest mr-2">Places</span>{s.booked}/{s.capacity}</p>
                      {s.notes && <p className="text-white/50 text-xs pt-2 border-t border-white/5">{s.notes}</p>}
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="font-display text-2xl text-[#C8A75A]">{s.price_eur > 0 ? `${s.price_eur} €` : "Sur devis"}</span>
                      <button
                        type="button"
                        disabled={full}
                        onClick={() => openBooking(s)}
                        data-testid={`book-session-${s.id}`}
                        className={`px-5 py-3 rounded-md uppercase tracking-widest text-[11px] font-medium transition-colors ${full ? "bg-white/5 text-white/30 cursor-not-allowed" : "bg-[#C8A75A] text-black hover:bg-white"}`}
                      >
                        {full ? "Complet" : "Réserver"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* BOOKING DIALOG */}
      {bookingOpen && (
        <div data-testid="booking-dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setBookingOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#1E1E1E] border border-[#C8A75A]/30 rounded-md w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto">
            {qualifyStep ? (
              <div data-testid="sim-qualify-step">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-[#C8A75A]/15 text-[#C8A75A] flex items-center justify-center">
                    <Check className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A]">Demande enregistrée</p>
                    <p className="text-white/60 text-xs">Nous vous recontactons sous 24 h ouvrables.</p>
                  </div>
                </div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-2 mt-6">Une dernière chose avant votre session</p>
                <h3 className="font-display text-2xl text-white mb-3 leading-tight">Quel type de véhicule vous fait rêver en ce moment ?</h3>
                <p className="text-white/60 text-sm mb-6">On vous tiendra informé en priorité si quelque chose correspondant arrive en stock.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["Porsche 911", "Cayman / Boxster", "Youngtimer", "Autre"].map((c) => {
                    const active = qualifyChoice === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        disabled={qualifySending}
                        onClick={() => submitQualify(c)}
                        data-testid={`sim-qualify-${c.replace(/[^\w]+/g, '-').toLowerCase()}`}
                        className={`px-4 py-4 rounded-md border text-sm uppercase tracking-widest transition-colors font-medium ${
                          active
                            ? "bg-[#C8A75A] border-[#C8A75A] text-black"
                            : "border-white/15 text-white hover:border-[#C8A75A]/60 hover:bg-[#C8A75A]/10 hover:text-[#C8A75A]"
                        } disabled:opacity-50`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
                  <button type="button" onClick={() => setBookingOpen(false)} data-testid="sim-qualify-skip" className="text-white/50 hover:text-white text-xs uppercase tracking-widest">
                    Passer cette étape
                  </button>
                  {qualifySending && <span className="text-white/40 text-xs">Envoi…</span>}
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-2">Réservation · Coaching sim</p>
                <h3 className="font-display text-2xl text-white mb-2">
                  {selectedSession
                    ? `${selectedSession.module} · ${new Date(selectedSession.date).toLocaleDateString("fr-BE", { day: "numeric", month: "long" })} à ${selectedSession.time}`
                    : "Session sur mesure"}
                </h3>
                {selectedSession?.circuit && <p className="text-white/60 text-sm mb-6">Circuit : {selectedSession.circuit} · Coach : {selectedSession.coach}</p>}
                <form onSubmit={submitBooking} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <input required data-testid="book-form-name" value={bookingForm.name} onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })} placeholder="Nom complet" className="col-span-2 bg-[#0D0D0D] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#C8A75A]/50 outline-none" />
                    <input required type="email" data-testid="book-form-email" value={bookingForm.email} onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })} placeholder="Email" className="bg-[#0D0D0D] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#C8A75A]/50 outline-none" />
                    <input type="tel" data-testid="book-form-phone" value={bookingForm.phone} onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })} placeholder="Téléphone" className="bg-[#0D0D0D] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#C8A75A]/50 outline-none" />
                    <select data-testid="book-form-level" value={bookingForm.level} onChange={(e) => setBookingForm({ ...bookingForm, level: e.target.value })} className="bg-[#0D0D0D] border border-white/10 rounded-md px-4 py-3 text-sm text-white focus:border-[#C8A75A]/50 outline-none">
                      <option value="débutant">Débutant · découverte</option>
                      <option value="amateur">Amateur · trackday</option>
                      <option value="expert">Expert · compétition amateur</option>
                      <option value="pro">Pro · compétition officielle</option>
                    </select>
                    <input data-testid="book-form-circuit" value={bookingForm.circuit_pref} onChange={(e) => setBookingForm({ ...bookingForm, circuit_pref: e.target.value })} placeholder="Circuit préféré (optionnel)" className="bg-[#0D0D0D] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#C8A75A]/50 outline-none" />
                  </div>
                  <textarea data-testid="book-form-message" rows={3} value={bookingForm.message} onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })} placeholder="Vos objectifs, questions, préférences horaires…" className="w-full bg-[#0D0D0D] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#C8A75A]/50 outline-none" />
                  <div className="flex items-center justify-between gap-3 pt-2">
                    <button type="button" onClick={() => setBookingOpen(false)} className="text-white/50 hover:text-white text-sm uppercase tracking-widest">Annuler</button>
                    <button type="submit" disabled={bookingSubmitting} data-testid="book-form-submit" className="bg-[#C8A75A] text-black hover:bg-white disabled:opacity-50 px-8 py-3 rounded-md uppercase tracking-widest text-xs font-semibold">
                      {bookingSubmitting ? "Envoi…" : "Envoyer ma demande"}
                    </button>
                  </div>
                  <p className="text-white/40 text-[10px] leading-relaxed">Vos données ne sont utilisées que pour organiser votre session. Nous vous recontactons sous 24 h ouvrables.</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Blocks */}
      <section className="py-24 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* STATS BAND */}
          <div data-testid="sim-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-24">
            {[
              { n: "142", label: "Pilotes formés", icon: Users },
              { n: "4,9/5", label: "Satisfaction", icon: Star },
              { n: "800 h", label: "Simulation", icon: Clock },
              { n: "3", label: "Modules", icon: Trophy },
            ].map((s, i) => (
              <div key={i} className="bg-[#1E1E1E] border border-white/10 rounded-md p-6 md:p-8 text-center hover:border-[#C8A75A]/40 transition-colors">
                <s.icon className="w-6 h-6 text-[#C8A75A] mx-auto mb-4" strokeWidth={1.3} />
                <p className="font-display text-4xl md:text-5xl text-white leading-none">{s.n}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-3">{s.label}</p>
              </div>
            ))}
          </div>

          {/* VIDEO TEASER — big playable card */}
          <div data-testid="sim-teaser" className="mb-24">
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
              <button
                data-testid="sim-teaser-play"
                onClick={() => setVideoOpen(true)}
                className="group relative aspect-[16/9] rounded-md overflow-hidden bg-black border border-white/10 hover:border-[#C8A75A]/50 transition-colors cursor-pointer"
              >
                {site.sim_video_url && (
                  <video key={site.sim_video_url} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity">
                    <source src={toStreamableVideoUrl(site.sim_video_url)} type="video/mp4" />
                  </video>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:bg-[#C8A75A]/95 group-hover:border-[#C8A75A] transition-all group-hover:scale-110">
                    <Play className="w-8 h-8 md:w-10 md:h-10 text-white group-hover:text-black ml-1" fill="currentColor" />
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 right-6 text-left">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A]">Teaser · 60 sec</p>
                  <p className="font-display text-xl md:text-2xl text-white mt-2">Une session simulateur en une minute</p>
                </div>
              </button>

              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Le voir avant de le vivre</p>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight font-light text-white mb-6">Un aperçu vaut mille mots.</h3>
                <p className="text-white/70 leading-relaxed mb-6">
                  Découvrez en 60 secondes l'ambiance d'une session : accueil au showroom, briefing pilote, immersion sur le rig dynamique, débriefing télémétrique autour d'un café.
                </p>
                <Link to="/contact?type=simulator" className="inline-flex items-center gap-2 bg-[#C8A75A] text-black hover:bg-white transition-colors px-8 py-4 rounded-md text-sm uppercase tracking-widest font-medium">
                  Je réserve <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.simulator.block_kicker}</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white max-w-3xl">{t.simulator.block_title}</h2>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {blocks.map((b, i) => (
              <div key={i} data-testid={`sim-block-${i+1}`} className="bg-[#1E1E1E] border border-white/10 rounded-md p-10 hover:border-[#C8A75A]/40 transition-colors duration-500 relative">
                <span className="absolute top-6 right-8 font-display text-6xl text-[#C8A75A]/15">0{i+1}</span>
                <b.icon className="w-9 h-9 text-[#C8A75A] mb-8" strokeWidth={1.2} />
                <h3 className="font-display text-2xl text-white mb-4 leading-tight">{b.title}</h3>
                <p className="text-white/60 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO GALLERY (reels) */}
      {reels.length > 0 && (
        <section data-testid="sim-videos" className="py-32 bg-[#1E1E1E]/40 border-y border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.simulator.videos_kicker}</p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-4">{(site.sim_videos_title && (site.sim_videos_title.fr || site.sim_videos_title.en)) ? (site.sim_videos_title.fr || site.sim_videos_title.en) : t.simulator.videos_title}</h3>
            <p className="text-white/60 mb-12 max-w-2xl">{t.simulator.videos_sub}</p>

            <div className="grid md:grid-cols-3 gap-4">
              {reels.map((url, i) => (
                <VideoCard key={i} src={url} idx={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* YOUTUBE REFERENCES */}
      {youtubeIds.length > 0 && (
        <section data-testid="sim-youtube" className="py-32">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.simulator.youtube_kicker}</p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-4">{t.simulator.youtube_title}</h3>
            <p className="text-white/60 mb-12 max-w-3xl">{t.simulator.youtube_sub}</p>

            <div className="grid md:grid-cols-3 gap-4">
              {youtubeIds.map((id, i) => (
                <div key={i} data-testid={`yt-embed-${i}`} className="relative aspect-video bg-black rounded-md overflow-hidden border border-white/10 group">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`}
                    title={`Sim racing reference ${i+1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SESSIONS GALLERY — vide par défaut. Le contenu doit venir de sessions réelles gérées via l'admin */}
      {Array.isArray(site.sim_gallery) && site.sim_gallery.length > 0 && (
        <section data-testid="sim-sessions-gallery" className="py-32 bg-[#0D0D0D]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Sessions récentes</p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-4">Ils ont pris le volant.</h3>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {site.sim_gallery.map((s, i) => (
                <div key={i} className="group relative aspect-[4/3] rounded-md overflow-hidden bg-[#1E1E1E] border border-white/10 hover:border-[#C8A75A]/50 transition-colors">
                  <img src={s.img} alt={s.title || `Session ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    {s.note && <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{s.note}</p>}
                    {s.title && <p className="text-white font-display text-lg leading-tight mt-1">{s.title}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRO REVIEWS */}
      <section data-testid="sim-timeline" className="py-32 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Déroulé d'une session</p>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-14">Comment ça se passe ?</h3>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { n: "01", d: "15 min", t: "Accueil showroom", s: "Café, présentation du rig et de vos objectifs." },
              { n: "02", d: "10 min", t: "Briefing pilote", s: "Circuit choisi, réglages du siège, du volant et des pédales." },
              { n: "03", d: "60 min", t: "Session sur simulateur", s: "Runs libres puis guidés, coaching en direct via casque." },
              { n: "04", d: "20 min", t: "Analyse télémétrique", s: "Comparaison de vos tours, détection des points à corriger." },
              { n: "05", d: "15 min", t: "Debrief café", s: "Programme personnalisé pour vos prochaines sessions." },
            ].map((s, i) => (
              <div key={i} className="bg-[#1E1E1E] border border-white/10 rounded-md p-6 relative hover:border-[#C8A75A]/40 transition-colors">
                <span className="font-display text-5xl text-[#C8A75A]/25 leading-none">{s.n}</span>
                <p className="text-[10px] uppercase tracking-widest text-[#C8A75A] mt-3">{s.d}</p>
                <p className="text-white font-medium mt-1">{s.t}</p>
                <p className="text-white/50 text-xs mt-2 leading-relaxed">{s.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section data-testid="sim-compare" className="py-32">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Comparatif</p>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-14">Piste vs Simulateur KV.</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Journée piste classique</p>
              <p className="font-display text-3xl text-white mb-6">Dès 800 € / jour</p>
              <ul className="space-y-3 text-sm">
                {["Location de piste + assurance","Usure des pneus et des freins","Météo aléatoire (annulation possible)","Risque de casse mécanique","Coach externe à payer en plus","Temps limité en volant"].map((x,i) => (
                  <li key={i} className="flex items-start gap-3 text-white/60 border-b border-white/5 pb-3"><span className="text-red-400 mt-0.5">✕</span>{x}</li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-b from-[#C8A75A]/10 to-transparent border border-[#C8A75A]/40 rounded-md p-8 relative">
              <span className="absolute -top-3 left-8 text-[10px] uppercase tracking-widest bg-[#C8A75A] text-black px-3 py-1 rounded-sm">Choix malin</span>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A] mb-3">Session simulateur KV</p>
              <p className="font-display text-3xl text-[#C8A75A] mb-6">Dès 89 € / session</p>
              <ul className="space-y-3 text-sm">
                {["Coaching pilote inclus","Zéro usure — pneus, freins, mécanique","Toutes conditions (nuit, pluie, brouillard)","Aucun risque de casse","Analyse télémétrique gratuite","Sessions personnalisées, à votre rythme"].map((x,i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 border-b border-white/5 pb-3"><span className="text-[#C8A75A] mt-0.5">✓</span>{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="sim-faq" className="py-32 bg-[#1E1E1E]/30 border-y border-white/5">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Questions fréquentes</p>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-12">Vos questions, nos réponses.</h3>
          <div className="space-y-3">
            {[
              { q: "Faut-il déjà savoir conduire vite ?", a: "Absolument pas. Nos programmes sont adaptés à tous les niveaux — débutant total, pilote loisir, ou compétiteur confirmé. Nous ajustons le circuit, la voiture virtuelle et le rythme à votre profil." },
              { q: "Combien de sessions pour progresser réellement ?", a: "Dès la première session vous ressentez le rig dynamique. Une progression mesurable en chrono s'installe généralement à partir de 3 sessions. Le Pack Progression (3 sessions) est notre formule la plus populaire pour cette raison." },
              { q: "Puis-je venir accompagné ?", a: "Oui, les sessions duo sont possibles — un pilote au rig, un compagnon spectateur avec écran deporté. Idéal pour partager l'expérience ou se challenger à tour de rôle." },
              { q: "Est-ce adapté aux enfants / ados ?", a: "À partir de 14 ans avec accompagnement parental. Le rig s'ajuste morphologiquement. Sessions découverte particulièrement plébiscitées comme cadeau." },
              { q: "Combien de temps à l'avance dois-je réserver ?", a: "Uniquement sur rendez-vous. Nous fonctionnons en soirée et week-end principalement — comptez idéalement 5-7 jours de délai pour caler le créneau qui vous convient." },
              { q: "Le simulateur reproduit vraiment le comportement d'une vraie voiture ?", a: "Notre rig dynamique intègre les transferts de masse (roulis, tangage, freinage), les remontées d'informations pédale (freinage progressif jusqu'à l'ABS) et volant (perte d'adhérence). Beaucoup plus proche d'une vraie piste qu'un simulateur statique de salon." },
            ].map((f, i) => (
              <details key={i} data-testid={`faq-${i}`} className="group bg-[#0D0D0D] border border-white/10 rounded-md hover:border-[#C8A75A]/40 transition-colors">
                <summary className="flex items-center justify-between gap-4 p-6 cursor-pointer list-none">
                  <span className="text-white font-medium">{f.q}</span>
                  <span className="w-8 h-8 rounded-full border border-[#C8A75A]/40 text-[#C8A75A] flex items-center justify-center flex-shrink-0 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <div className="px-6 pb-6 -mt-2 text-white/70 leading-relaxed text-sm">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* PRO REVIEWS ORIGINAL */}
      <section data-testid="sim-reviews" className="py-32 bg-[#1E1E1E]/30 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.simulator.reviews_kicker}</p>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-14">{t.simulator.reviews_title}</h3>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <div key={i} data-testid={`sim-review-${i}`} className="bg-[#0D0D0D] border border-white/10 rounded-md p-8 flex flex-col">
                <span className="font-display text-6xl text-[#C8A75A]/25 leading-none mb-4">"</span>
                <p className="text-white/80 leading-relaxed italic flex-1">{r.quote}</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white font-medium">{r.name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#C8A75A] mt-1">{r.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING GRID */}
      {prices.length > 0 && (
        <section data-testid="sim-pricing" className="py-32">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Tarifs</p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-4">Choisissez votre formule.</h3>
            <p className="text-white/60 mb-14 max-w-2xl">Chaque session dure 60 min : briefing, run libre, débriefing télémétrique. Sur rendez-vous, en soirée ou week-end.</p>

            <div className="grid md:grid-cols-3 gap-6">
              {prices.map((p, i) => {
                const featured = p.featured === "1" || p.featured === true || p.featured === "true";
                return (
                  <div
                    key={i}
                    data-testid={`sim-price-${i}`}
                    className={`relative rounded-md p-10 flex flex-col transition-all duration-500 ${
                      featured
                        ? "bg-[#C8A75A] text-black border border-[#C8A75A] shadow-[0_20px_60px_rgba(200,167,90,0.25)] -translate-y-2"
                        : "bg-[#1E1E1E] text-white border border-white/10 hover:border-[#C8A75A]/40"
                    }`}
                  >
                    {featured && (
                      <span className="absolute -top-3 left-8 text-[10px] uppercase tracking-widest bg-black text-[#C8A75A] px-3 py-1 rounded-sm">Populaire</span>
                    )}
                    <p className={`text-[11px] uppercase tracking-[0.3em] mb-4 ${featured ? "text-black/60" : "text-[#C8A75A]"}`}>{p.label}</p>
                    <p className={`font-display text-5xl md:text-6xl leading-none ${featured ? "text-black" : "text-white"}`}>{p.price}</p>
                    <p className={`text-sm mt-4 leading-relaxed flex-1 ${featured ? "text-black/70" : "text-white/60"}`}>{p.note}</p>
                    <Link
                      to="/contact?type=simulator"
                      data-testid={`sim-price-cta-${i}`}
                      className={`mt-8 text-xs uppercase tracking-widest px-6 py-3 rounded-md text-center transition-colors inline-flex items-center justify-center gap-2 ${
                        featured
                          ? "bg-black text-[#C8A75A] hover:bg-white hover:text-black"
                          : "border border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black"
                      }`}
                    >
                      Réserver <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                );
              })}
            </div>

            <p className="text-white/40 text-xs mt-8 text-center">Cartes cadeaux disponibles · TVA belge 21 % incluse · Paiement sur place ou virement</p>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/3] rounded-md overflow-hidden">
            <img src={RIG_IMG} alt="rig" className="w-full h-full object-cover img-fade" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0D0D0D]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A]">Dynamique · Pro</p>
              <p className="font-display text-3xl">Sim rig · KV</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Bénéfices</p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-10">{t.simulator.benefits_title}</h3>
            <ul className="space-y-4">
              {t.simulator.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-4 text-white/80 border-b border-white/5 pb-4">
                  <Check className="w-5 h-5 text-[#C8A75A] mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SOCIAL PROMOTION */}
      <section data-testid="sim-social" className="py-32 bg-gradient-to-b from-[#0D0D0D] via-[#1E1E1E]/40 to-[#0D0D0D] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.simulator.social_kicker}</p>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-4">{t.simulator.social_title}</h3>
          <p className="text-white/60 mb-10 max-w-2xl mx-auto">{t.simulator.social_text}</p>

          <div className="flex flex-wrap justify-center gap-4">
            <SocialBtn href={site.tiktok} icon={TikTokIcon} label="TikTok" color="#25F4EE" testid="social-tiktok" fallbackHref="https://www.tiktok.com/search?q=kv%20international" />
            <SocialBtn href={site.instagram} icon={Instagram} label="Instagram" color="#E1306C" testid="social-instagram" fallbackHref="https://www.instagram.com/explore/tags/kvinternational/" />
            <SocialBtn href={site.facebook} icon={Facebook} label="Facebook" color="#1877F2" testid="social-facebook" fallbackHref="https://www.facebook.com/search/top?q=kv%20international" />
            <SocialBtn href={site.youtube} icon={Youtube} label="YouTube" color="#FF0000" testid="social-youtube" fallbackHref="https://www.youtube.com/results?search_query=kv+international+simulateur" />
          </div>

          <div className="mt-16 pt-16 border-t border-white/10">
            <p className="font-display text-xl md:text-2xl text-white mb-2">Partagez votre session avec le hashtag</p>
            <p className="font-brand text-[#C8A75A] text-xl md:text-2xl mb-8">#KVSimStage</p>
            <Link
              to="/share"
              data-testid="sim-share-kit"
              className="inline-flex items-center gap-2 border border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black transition-colors duration-300 px-8 py-4 rounded-md text-sm uppercase tracking-widest"
            >
              Kit de partage & QR code <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <Trophy className="w-10 h-10 text-[#C8A75A] mx-auto mb-8" strokeWidth={1.2} />
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">{t.simulator.cta_title}</h2>
          <p className="text-white/60 text-lg mb-10">{t.simulator.cta_text}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact?type=simulator" data-testid="sim-cta-book" className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-medium px-10 py-5 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
              {t.simulator.book_btn} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={waHref} target="_blank" rel="noreferrer" className="bg-[#25D366] text-black hover:bg-white transition-colors duration-300 font-medium px-10 py-5 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} src={toStreamableVideoUrl(site.sim_video_url) || "/assets/showroom.mp4"} />
    </div>
  );
}

function VideoCard({ src, idx }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div data-testid={`sim-video-${idx}`} className="relative aspect-[9/16] md:aspect-[4/5] bg-black rounded-md overflow-hidden border border-white/10 group cursor-pointer" onClick={() => setPlaying(true)}>
      <video
        src={src}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:bg-[#C8A75A]/90 group-hover:border-[#C8A75A] transition-colors">
            <Play className="w-6 h-6 text-white group-hover:text-black ml-1" fill="currentColor" />
          </div>
        </div>
      )}
      <div className="absolute bottom-4 left-4 right-4">
        <p className="text-[10px] uppercase tracking-widest text-[#C8A75A]">Session {String(idx + 1).padStart(2, "0")}</p>
      </div>
    </div>
  );
}

function SocialBtn({ href, icon: Icon, label, color, testid, fallbackHref }) {
  const url = href || fallbackHref;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="group flex items-center gap-3 bg-[#1E1E1E] border border-white/10 hover:border-[#C8A75A] rounded-md px-6 py-4 transition-all duration-300 hover:-translate-y-1"
      style={{ boxShadow: `0 0 0 rgba(0,0,0,0)` }}
    >
      <span
        className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
        style={{ background: `${color}20`, color }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <div className="text-left">
        <p className="text-[10px] uppercase tracking-widest text-white/50">Follow</p>
        <p className="text-sm font-medium text-white group-hover:text-[#C8A75A] transition-colors">{label}</p>
      </div>
    </a>
  );
}
